<template>
  <div class="chart-base">
    <!-- Tooltip remains the same structure -->
    <Teleport to="body">
      <div id="chart-tooltip" style="display: none" class="chart-tooltip container">
        <div class="content">
          <div id="row-x" class="row row-x">
            <div class="col">
              <TextBase class="info" text="Info" color="primary" weight="bold" type="tiny" />
            </div>
          </div>
          <div id="row-y" class="row-y">
            <div class="col">
              <svg
                width="3"
                height="100%"
                preserve-aspect-ratio="none"
                view-box="0 0 3 24"
                class="tooltip-legend"
              >
                <g>
                  <rect width="100%" height="100%" fill="red" class="background-rect"></rect>
                  <rect width="100%" height="100%" fill="green" class="foreground-rect"></rect>
                </g>
              </svg>
            </div>
            <div class="col">
              <div class="row flex-1">
                <TextBase class="label" text="Label" color="secondary" weight="bold" type="tiny" />
              </div>
              <div class="row flex-1">
                <TextBase class="value" text="-" type="label" weight="bold" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    <!-- SVG Chart remains the same structure -->
    <svg ref="svgRef" class="chart" :width="viewWidth" :height="viewHeight" :viewBox="viewBox">
      <defs></defs>
      <g
        ref="groupRef"
        :width="width"
        :height="height"
        :transform="`translate(${padding.left}, ${padding.top})`"
        :data-padding="JSON.stringify(padding)"
      >
        <component
          :is="componentMap[component.type]"
          v-for="component in componentsWithId"
          :key="component.id + computedHeight"
          v-bind="component"
        />
      </g>
    </svg>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue'
import { ChartComponent } from '.'
import { componentMap } from './components'
import md5 from 'md5'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { getTextWidth } from '@/utils/text-width'

const props = withDefaults(
  defineProps<{
    height?: string
    components: ChartComponent[]
    paddingOverride?: {
      top?: number
      right?: number
      bottom?: number
      left?: number
    }
  }>(),
  {
    height: '100%',
    paddingOverride: () => ({})
  }
)

const { height: containerHeight, components } = toRefs(props)

const svgRef = ref<SVGSVGElement | null>(null)
const groupRef = ref<SVGSVGElement | null>(null)
const padding = computed(() => {
  const leftAxis = props.components.find((c) => c.type === 'axis' && c.position === 'left') as
    | ChartComponent<'axis'>
    | undefined
  const bottomAxis = props.components.find((c) => c.type === 'axis' && c.position === 'bottom') as
    | ChartComponent<'axis'>
    | undefined
  const maxLength =
    (leftAxis?.tickLabels ?? leftAxis?.ticks.map((t) => t.toString()))?.reduce(
      (a, b) => (a.length > b.length ? a : b),
      ''
    ) ?? ''
  const fontSize = leftAxis?.font?.size ?? 10
  const font = leftAxis?.font?.family ?? 'Inter'
  const textWidth = getTextWidth(maxLength, `${fontSize}px ${font}`)
  const leftPadding = textWidth + 14
  const bottomPadding = 30 + textWidth * Math.sin((bottomAxis?.rotate ?? 0) * (Math.PI / 180))
  return {
    top: 30,
    right: 20,
    bottom: bottomPadding,
    left: leftPadding,
    ...props.paddingOverride
  }
})

const viewWidth = ref<number | undefined>(undefined)
const viewHeight = ref<number | undefined>(undefined)

const width = computed(() =>
  viewWidth.value ? viewWidth.value - padding.value.right - padding.value.left : undefined
)
const height = computed(() =>
  viewHeight.value ? viewHeight.value - padding.value.top - padding.value.bottom : undefined
)

const viewBox = computed(() => `0 0 ${viewWidth.value ?? 0} ${viewHeight.value ?? 0}`)

const componentsWithId = computed(() =>
  components.value.map((c) => ({ ...c, id: md5(JSON.stringify(c)) }))
)

const computedHeight = computed(() => containerHeight?.value ?? '100%')

watch(svgRef, () => {
  if (!svgRef.value) return
  new ResizeObserver((entries) => {
    const { width, height } = entries[0].contentRect
    viewWidth.value = width
    viewHeight.value = height
  }).observe(svgRef.value)
})
</script>

<style lang="scss" scoped>
.chart-base {
  width: 100%;
  // height: v-bind(computedHeight);
  height: 100%;
  position: relative;

  .chart {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>

<style lang="scss">
.chart-tooltip.container {
  padding: 12px 16px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));
  pointer-events: none;

  .content {
    background-color: #f9f9f9;
    border-radius: 4px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &.after-init {
    transition: transform 0.05s ease-in-out;
  }

  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 11px;
    background-color: #f9f9f9;
    clip-path: path(
      'M8.46356 10.1563C9.26315 11.1158 10.7369 11.1158 11.5364 10.1563L20 3.49691e-06L2.09815e-06 0L8.46356 10.1563Z'
    );
  }

  &.top::before {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, -4px);
  }

  &.bottom::before {
    top: 0;
    left: 50%;
    transform: translate(-50%, 4px) rotate(180deg);
  }

  &.left::before {
    top: 50%;
    right: 0;
    transform: translate(4px, -50%) rotate(-90deg);
  }

  &.right::before {
    top: 50%;
    left: 0;
    transform: translate(-4px, -50%) rotate(90deg);
  }

  .tooltip-legend {
    position: absolute;
    display: block;
    margin-right: 16px;
    width: 3px;
    height: 100%;
  }

  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;

    .flex-1 {
      flex: 1;
    }

    .col {
      flex-direction: column;
      display: flex;
    }
  }

  .row-y {
    display: grid;
    grid-template-columns: 16px 1fr;
    grid-auto-rows: 1fr;
    align-items: center;

    .value {
      padding-top: 4px;
    }

    .col {
      position: relative;
      display: flex;
      overflow: hidden;
      flex-direction: column;
      height: 100%;
    }
  }
}
</style>
